import { IslandRegistry, type IslandComponent } from '@cogo/react-renderer'

/** Adapt a code-split island module to the registry's loader shape. Island
 * components declare specific prop types; the IslandRenderer feeds them
 * resolveSymbol-evaluated `Record<string, unknown>` props, so the cast to the
 * registry's `IslandComponent` is the (intended) host↔island prop contract. */
const lazyDefault = (m: { default: unknown }): { default: IslandComponent } => ({
  default: m.default as IslandComponent,
})

/**
 * STUDIO_PREVIEW_ISLANDS — the host island registry threaded into EVERY studio
 * AppV2 generated-app preview surface (preview-frame-content + build-mode-canvas)
 * so a generated app's `type:"island"` nodes can mount. One module-scope
 * singleton (shared, not per-file) = stable identity across re-renders (AppV2's
 * ctx memo doesn't churn) AND a single place to register islands.
 *
 * Game / interactive-render islands (`@cogo/react-renderer-game`) are registered
 * here LAZILY. `registerLazy` only stores a loader; the heavy WebGL runtime
 * (@pixi/react + pixi.js for game-2d, three + @react-three/fiber + drei for
 * game-3d) lives in a code-split chunk that is fetched ONLY when a generated app
 * actually mounts a `cogor.game.*` island node — i.e. an app whose manifest
 * declares a game render lane (`meta.renderProfile.lanes` ∋ game-2d / game-3d).
 * Non-game previews never trigger the loader, so they never pull the WebGL
 * bundle — lazy loading IS the lane gate at runtime.
 *
 * We register via the package's subpath exports (`@cogo/react-renderer-game/…`)
 * rather than its `registerGameIslands()` helper: that helper's relative
 * `import("./X.js")` self-imports don't resolve under a source/Vite build, while
 * the subpath dynamic import resolves to the .tsx source AND code-splits cleanly.
 * Island names mirror cogor:islandName (render-patterns-v1.ttl) / GAME_ISLAND_NAMES.
 */
export const STUDIO_PREVIEW_ISLANDS = new IslandRegistry()

STUDIO_PREVIEW_ISLANDS.registerLazy('cogor.game.pixi-stage', () =>
  import('@cogo/react-renderer-game/PixiStageIsland').then(lazyDefault),
)
STUDIO_PREVIEW_ISLANDS.registerLazy('cogor.game.r3f-scene', () =>
  import('@cogo/react-renderer-game/R3FSceneIsland').then(lazyDefault),
)
